import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buildElement } from './elementMap';
import {
  ActionButtonProps,
  ActionElementType,
  ActionButtonRenderContext
} from './types';
import { AnyFunc } from '@/utils/types';

/**
 * 判断是否为thenable对象(promise对象)
 * @template T
 * @param {*} [thing]
 * @return {*}  {thing is PromiseLike<T>}
 */
const isThenable = function <T>(thing?: any): thing is PromiseLike<T> {
  return !!thing && typeof thing.then === 'function';
};

/**
 * 自定义button组件
 * 内置防抖、loading态设置
 * 可选按钮样式，在/elementMap.tsx处新增按钮样式
 * @param {ActionButtonProps} props
 * @return {*}
 */
const ActionButton: React.FC<ActionButtonProps> = function (
  props: ActionButtonProps
) {
  const as = props.as || 'button';
  const children = props.children;
  const disabled = !!props.disabled;
  const clickableWhenDisabled = !!props.clickableWhenDisabled;
  const className = props.className || '';
  const style = props.style || {};
  const onOk = props.onOk;
  const clickFinishedCb = props.clickFinishedCb;
  const href = props.href;
  const target = props.target;
  const stopPropagation = props.stopPropagation !== false;
  const preventDefault = props.preventDefault !== false;
  const preventTouchScroll = props.preventTouchScroll !== false;
  const autoFocus = !!props.autoFocus;
  const amType = props.amType || 'primary';
  const amFill = props.amFill || 'solid';
  const amSize = props.amSize || 'middle';
  const amBlock = !!props.amBlock;
  const touchAction = props.touchAction || 'manipulation';
  const overscrollBehavior = props.overscrollBehavior || 'contain';

  const clickedRef = useRef<boolean>(false); // 防抖锁（是否点击过此按钮）
  const [loading, setLoading] = useState<boolean>(false); // 按钮是否处于loading态
  const btnRef = useRef<HTMLButtonElement | HTMLAnchorElement | null>(null); // 按钮的dom元素（用于阻止滚动事件）
  const touchMoveGuardRef = useRef<any>(null); // 用于阻止滚动事件

  /**
   * 页面挂载--自动聚焦按钮（可选）且尽量避免页面滚动跳动
   * - 当 autoFocus 为 true 时，在组件渲染完成后将焦点移到按钮，便于键盘／无障碍操作
   * - 使用 focus({ preventScroll: true }) 尝试阻止浏览器因聚焦而滚动到该元素位置，减少“跳屏”
   */
  useEffect(
    function () {
      // 定时器 ID，用于在清理阶段取消
      let id: any = null;
      if (autoFocus) {
        // 延迟到下一轮事件循环，等待布局稳定后再聚焦
        id = setTimeout(function () {
          try {
            // 使用 preventScroll 避免聚焦导致的页面滚动；部分旧浏览器不支持该参数
            if (btnRef.current && (btnRef.current as any).focus) {
              (btnRef.current as any).focus({ preventScroll: true });
            }
          } catch (_) {}
        }, 0);
      }
      // 清理未执行的定时器，避免内存泄漏或卸载后仍操作 DOM
      return function () {
        if (id) clearTimeout(id);
      };
    },
    [autoFocus]
  );

  /**
   * 页面卸载--移除滚动事件
   */
  useEffect(function () {
    return function () {
      removeTouchMoveGuard();
    };
  }, []);

  /**
   * 点击按钮期间，阻止滚动事件
   * @return {*}
   */
  const addTouchMoveGuard = function () {
    if (!preventTouchScroll) return;
    if (touchMoveGuardRef.current) return;
    // 定义一个touchmove事件处理函数
    const handler = function (e: TouchEvent) {
      try {
        e.preventDefault();
      } catch (err) {}
    };
    touchMoveGuardRef.current = handler;
    // 添加touchmove事件监听器
    // 并指定passive为false，以确保在事件处理函数中可以调用preventDefault()方法
    try {
      document.addEventListener(
        'touchmove',
        handler as any,
        { passive: false } as any
      );
    } catch (e) {
      document.addEventListener('touchmove', handler as any);
    }
  };

  /**
   * 移除滚动事件
   */
  const removeTouchMoveGuard = function () {
    if (touchMoveGuardRef.current) {
      document.removeEventListener(
        'touchmove',
        touchMoveGuardRef.current as any
      );
      touchMoveGuardRef.current = null;
    }
  };

  /**
   * 点击按钮结束，重置状态
   * @param {boolean} [isAsync]
   */
  const finishClick = function (isAsync?: boolean) {
    // 异步按钮点击结束，loading态取消
    isAsync && setLoading(false);
    // 重置防抖锁
    clickedRef.current = false;
    // 移除滚动事件
    removeTouchMoveGuard();
  };

  /**
   * 执行外部按钮点击/成功的回调
   * @param {...any[]} args
   */
  const onClickFinishedCb = function (...args: any[]) {
    clickFinishedCb && clickFinishedCb.apply(null, args);
  };

  /**
   * 处理onOk返回promise的情况
   * @param {Promise<any>} [returnValueOfOnOk]
   * @return {*}
   */
  const handlePromiseOnOk = function (returnValueOfOnOk?: Promise<any>) {
    if (!isThenable(returnValueOfOnOk)) return;
    // 按钮loading态开启
    setLoading(true);
    // 等待promise执行结束
    Promise.resolve(returnValueOfOnOk)
      .then(function () {
        // 成功结束后，重置状态，执行外部回调
        onClickFinishedCb();
        finishClick(true);
      })
      .catch(function (_) {
        // 失败结束后仅重置状态
        finishClick(true);
      });
  };

  /**
   * 处理按钮点击，执行外部点击函数onOk
   * @param {AnyFunc} [func]
   * @return {*}
   */
  const handleCommonOkClick = function (func?: AnyFunc) {
    // 当前按钮已点击过，再次点击则直接返回
    if (clickedRef.current) {
      return;
    }
    // 按钮点击开始，防抖锁开启
    clickedRef.current = true;
    // 开始监听滚动事件
    addTouchMoveGuard();

    // 没有onOk，直接执行外部回调并结束点击
    if (!func) {
      onClickFinishedCb();
      finishClick();
      return;
    }
    // 执行onOk
    let returnValueOfOnOk: Promise<any> | undefined = undefined;
    try {
      returnValueOfOnOk = func(onClickFinishedCb);
    } catch (error) {
      finishClick();
      return;
    }
    // onOk返回的不是promise，直接执行外部回调并结束点击
    if (!isThenable(returnValueOfOnOk)) {
      onClickFinishedCb();
      finishClick();
      return;
    }
    // onOk返回promise则进一步处理
    handlePromiseOnOk(returnValueOfOnOk);
  };

  const handleClick = function (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) {
    // 阻止冒泡
    if (stopPropagation && e.stopPropagation) e.stopPropagation();
    // 阻止默认行为
    if (preventDefault && e.preventDefault) e.preventDefault();

    // 进入onOk函数处理逻辑
    if (loading) return;
    if (disabled && !clickableWhenDisabled) return;
    handleCommonOkClick(onOk);
  };

  /**
   * 合并样式
   */
  const mergedStyle: React.CSSProperties = useMemo(
    function () {
      const base: React.CSSProperties = {};
      const keys = Object.keys(style || {});
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        (base as any)[k] = (style as any)[k];
      }
      (base as any).touchAction = touchAction;
      (base as any).overscrollBehavior = overscrollBehavior;
      if (clickableWhenDisabled && disabled) {
        if ((base as any).cursor == null) {
          (base as any).cursor = 'not-allowed';
        }
        if ((base as any).opacity == null) {
          (base as any).opacity = 0.5;
        }
      }
      return base;
    },
    [style, clickableWhenDisabled, disabled, touchAction, overscrollBehavior]
  );

  /**
   * 构造按钮渲染上下文
   */
  const renderCtx: ActionButtonRenderContext = {
    className: className,
    style: mergedStyle,
    children: children,
    loading: loading,
    disabled: loading || (disabled && !clickableWhenDisabled),
    handleClick: handleClick,
    btnRef: btnRef,
    href: href,
    target: target,
    amType: amType,
    amFill: amFill,
    amSize: amSize,
    amBlock: amBlock,
    popover: props.popover,
    'data-testid': props['data-testid']
  };

  return buildElement(as as ActionElementType, renderCtx);
};

export default ActionButton;
